import { HttpError } from '@/lib/httpError'
import type { ProductRecord } from '@/config/products'

/** Fields the owner may set on a product; omitted ones are left as they are. */
export interface ProductInput {
  name?: string
  description?: string
  icon?: string
  href?: string
  external?: boolean
  enabled?: boolean
}

async function expectOk(response: Response, failKey: string): Promise<Response> {
  if (response.ok) return response
  if (response.status === 403) throw new HttpError('errors.forbidden', 403)
  let message = failKey
  try {
    const body = await response.json()
    if (body && typeof body.error === 'string') message = body.error
  } catch {
    // keep the generic key
  }
  throw new HttpError(message, response.status)
}

const json = (body: unknown): RequestInit => ({
  headers: { 'Content-Type': 'application/json' },
  credentials: 'include',
  body: JSON.stringify(body),
})

/**
 * The console's product list, held by the auth service. Read by every
 * signed-in user; changed only by the installation's superuser accounts.
 */
class PlatformSettingsService {
  private baseUrl = '/api/v2/platform'

  /** The list, and whether this account may change it (an installation superuser). */
  async listProducts(): Promise<{ products: ProductRecord[]; can_manage: boolean }> {
    const r = await expectOk(await fetch(`${this.baseUrl}/products`, { credentials: 'include' }), 'errors.loadFailed')
    const body = await r.json()
    return { products: body.products as ProductRecord[], can_manage: body.can_manage === true }
  }

  async createProduct(input: ProductInput): Promise<ProductRecord> {
    const r = await expectOk(await fetch(`${this.baseUrl}/products`, { method: 'POST', ...json(input) }), 'errors.saveFailed')
    return (await r.json()).product as ProductRecord
  }

  async updateProduct(id: string, input: ProductInput): Promise<ProductRecord> {
    const r = await expectOk(await fetch(`${this.baseUrl}/products/${encodeURIComponent(id)}`, { method: 'PUT', ...json(input) }), 'errors.saveFailed')
    return (await r.json()).product as ProductRecord
  }

  async deleteProduct(id: string): Promise<void> {
    await expectOk(await fetch(`${this.baseUrl}/products/${encodeURIComponent(id)}`, { method: 'DELETE', credentials: 'include' }), 'errors.deleteFailed')
  }

  async reorderProducts(ids: string[]): Promise<ProductRecord[]> {
    const r = await expectOk(await fetch(`${this.baseUrl}/products-order`, { method: 'PUT', ...json({ ids }) }), 'errors.saveFailed')
    return (await r.json()).products as ProductRecord[]
  }
}

export const platformSettingsService = new PlatformSettingsService()
